'use client';
import React, { useEffect, useRef } from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

export default function RefundPage() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1 }
    );
    const els = sectionRef?.current?.querySelectorAll('.reveal');
    els?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  return (
    <main className="bg-canopy min-h-screen" ref={sectionRef}>
      <Header />

      <div className="pt-32 pb-20 px-6 md:px-10 blueprint-grid">
        <div className="max-w-3xl mx-auto">
          {/* Header */}
          <div className="mb-12">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-px bg-chartreuse opacity-60" />
              <span className="annotation">KEBIJAKAN REFUND</span>
            </div>
            <h1 className="font-mono font-bold text-4xl md:text-5xl text-text-primary mb-4 leading-tight">
              Kebijakan<br />
              <span className="text-chartreuse">Pengembalian.</span>
            </h1>
            <p className="text-text-muted text-sm">
              Terakhir diperbarui: 2025 · Agro Teknologi Nusantara
            </p>
          </div>

          <div className="space-y-10">
            {/* Pengembalian barang */}
            <section className="reveal">
              <h2 className="font-mono font-bold text-xl text-chartreuse mb-4">1. Pengembalian Barang</h2>
              <div className="text-text-muted text-sm leading-relaxed space-y-3">
                <p>
                  Pengembalian barang dapat dilakukan dalam waktu <strong className="text-text-primary">7 hari kerja</strong> terhitung
                  sejak tanggal penerimaan untuk produk yang:
                </p>
                <ul className="list-disc list-inside space-y-2 pl-4">
                  <li>rusak atau cacat saat diterima</li>
                  <li>tidak sesuai dengan deskripsi atau pesanan</li>
                </ul>
                <p>
                  Produk harus dikembalikan dalam kondisi asli, belum digunakan, dan dengan
                  kemasan asli lengkap. Hubungi kami di{' '}
                  <a href="mailto:ptagroteknologinusantara@gmail.com" className="text-chartreuse hover:underline">
                    ptagroteknologinusantara@gmail.com
                  </a>{' '}
                  dengan menyertakan nomor pesanan dan foto produk.
                </p>
              </div>
            </section>

            {/* Pengembalian dana */}
            <section className="reveal">
              <h2 className="font-mono font-bold text-xl text-chartreuse mb-4">2. Pengembalian Dana</h2>
              <div className="text-text-muted text-sm leading-relaxed space-y-3">
                <p>
                  Pengembalian dana (refund) hanya berlaku untuk:
                </p>
                <ul className="list-disc list-inside space-y-2 pl-4">
                  <li>Produk rusak/cacat atau tidak sesuai pesanan</li>
                  <li>Kondisi yang telah diverifikasi oleh tim Agro Teknologi Nusantara</li>
                </ul>
                <p>
                  Dana akan diproses dalam waktu <strong className="text-text-primary">14 hari kerja</strong> setelah
                  verifikasi selesai. Pengembalian dana akan dikembalikan melalui metode pembayaran
                  yang sama dengan yang digunakan saat pemesanan.
                </p>
              </div>
            </section>

            {/* Biaya pengiriman */}
            <section className="reveal">
              <h2 className="font-mono font-bold text-xl text-chartreuse mb-4">3. Biaya Pengiriman</h2>
              <div className="text-text-muted text-sm leading-relaxed space-y-3">
                <ul className="list-disc list-inside space-y-2">
                  <li>Biaya pengiriman untuk pengembalian barang adalah <strong className="text-text-primary">tanggung jawab pembeli</strong>, kecuali produk yang diterima memang cacat atau tidak sesuai pesanan.</li>
                  <li>Untuk kasus produk cacat atau tidak sesuai pesanan, Agro Teknologi Nusantara akan menanggung biaya pengiriman pengembalian setelah verifikasi.</li>
                  <li>Kami sarankan menggunakan jasa pengiriman dengan pelacakan (tracking) untuk keamanan paket.</li>
                </ul>
              </div>
            </section>

            {/* Layanan IoT & Pelatihan */}
            <section className="reveal">
              <h2 className="font-mono font-bold text-xl text-chartreuse mb-4">4. Layanan IoT & Pelatihan</h2>
              <div className="text-text-muted text-sm leading-relaxed space-y-3">
                <ul className="list-disc list-inside space-y-2">
                  <li>
                    <strong className="text-text-primary">Layanan IoT Berlangganan:</strong> Refund prorata berlaku jika
                    layanan tidak sesuai dengan yang dijanjikan setelah proses verifikasi. Hubungi
                    tim kami untuk diskusi klaim.
                  </li>
                  <li>
                    <strong className="text-text-primary">Pelatihan & Edukasi:</strong> Refund dapat diajukan dalam 7 hari
                    setelah pembayaran jika peserta tidak puas dengan layanan pelatihan. Pengajuan
                    refund harus disertai alasan yang jelas.
                  </li>
                  <li>
                    <strong className="text-text-primary">Produk IoT Kit:</strong> Sama  dengan pengembalian barang di atas.
                  </li>
                </ul>
              </div>
            </section>

            {/* Proses pengajuan */}
            <section className="reveal">
              <h2 className="font-mono font-bold text-xl text-chartreuse mb-4">5. Cara Mengajukan Refund</h2>
              <div className="text-text-muted text-sm leading-relaxed space-y-3">
                <ol className="list-decimal list-inside space-y-2 pl-4">
                  <li>Hubungi tim kami via email di ptagroteknologinusantara@gmail.com atau WhatsApp di +62 812 5383 7777</li>
                  <li>Sertakan nomor pesanan, deskripsi masalah, dan foto pendukung</li>
                  <li>Tim kami akan memproses verifikasi dalam 3 hari kerja</li>
                  <li>Setelah disetujui, refund akan diproses dalam 14 hari kerja</li>
                </ol>
              </div>
            </section>

            {/* Tidak eligible */}
            <section className="reveal">
              <h2 className="font-mono font-bold text-xl text-chartreuse mb-4">6. Kondisi Tidak Eligible</h2>
              <div className="text-text-muted text-sm leading-relaxed space-y-3">
                <p>Refund tidak berlaku untuk:</p>
                <ul className="list-disc list-inside space-y-2 pl-4">
                  <li>Produk yang sudah digunakan atau dimodifikasi</li>
                  <li>Produk yang rusak karena kesalahan pengguna</li>
                  <li>Layanan yang sudah digunakan sepenuhnya</li>
                  <li>Pengajuan refund setelah 7 hari kerja dari tanggal penerimaan</li>
                </ul>
              </div>
            </section>
          </div>

          {/* CTA */}
          <div className="reveal mt-16 border border-chartreuse/20 rounded-sm p-8 bg-pond/30 text-center">
            <p className="text-text-muted text-sm mb-4">
              Butuh bantuan dengan pengembalian atau refund?
            </p>
            <a
              href="/quote"
              className="btn-chartreuse px-6 py-3 rounded-sm text-xs uppercase tracking-widest inline-block"
            >
              Hubungi Kami →
            </a>
          </div>
        </div>
      </div>

      <Footer />
    </main>
  );
}
